import React, { lazy, Suspense, useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./toast-overrides.css";

import Preloader from "./components/Preloader";
import Header from "./components/Header";
import MobileHeader from "./components/MobileHeader";
import Footer from "./components/Footer";
import ProtectedRoute from "./components/ProtectedRoute";

// Lazy-loaded pages — each loads only when its route is visited
// HRMS & Accounts
const HRMSDashboard = lazy(() => import("./pages/HRMS").then(m => ({ default: m.HRMSDashboard })));
const AccountsDashboard = lazy(() => import("./pages/Accounts").then(m => ({ default: m.AccountsDashboard })));

// Public pages
const Home = lazy(() => import("./pages/Home"));
const JobGrid = lazy(() => import("./pages/JobGrid"));
const BrowseJobs = lazy(() => import("./pages/BrowseJobs"));
const JobDetails = lazy(() => import("./pages/JobDetails"));
const LocationsJobs = lazy(() => import("./pages/LocationsJobs"));
const CompanyDetails = lazy(() => import("./pages/CompanyDetails"));
const CandidatesGrid = lazy(() => import("./pages/CandidatesGrid"));
const BlogGrid = lazy(() => import("./pages/BlogGrid"));
const BlogDetail = lazy(() => import("./pages/BlogDetail"));
const NextGenTraining = lazy(() => import("./pages/NextGenTraining"));
const ContactUs = lazy(() => import("./pages/ContactUs"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const PublicInterviewLanding = lazy(() => import("./pages/PublicInterviewLanding"));
const Unauthorized = lazy(() => import("./pages/Unauthorized"));

// Candidate pages
const CandidateSignIn = lazy(() => import("./pages/candidate/CandidateSignIn"));
const CandidateSignUp = lazy(() => import("./pages/candidate/CandidateSignUp"));
const ManageProfile = lazy(() => import("./pages/candidate/ManageProfile"));
const AppliedJobs = lazy(() => import("./pages/candidate/AppliedJobs"));
const BookmarkedJobs = lazy(() => import("./pages/candidate/BookmarkedJobs"));
const ResumeBuilder = lazy(() => import("./pages/candidate/ResumeBuilder"));
const CandidateInterviews = lazy(() => import("./pages/candidate/CandidateInterviews"));
const InterviewRoom = lazy(() => import("./pages/candidate/InterviewRoom"));
const MyTransactions = lazy(() => import("./pages/candidate/MyTransactions"));

// Employer pages
const EmployerSignIn = lazy(() => import("./pages/employer/EmployerSignIn"));
const EmployerSignUp = lazy(() => import("./pages/employer/EmployerSignUp"));
const DashboardOverview = lazy(() => import("./pages/employer/DashboardOverview"));
const CompanyProfile = lazy(() => import("./pages/employer/CompanyProfile"));
const PostJob = lazy(() => import("./pages/employer/PostJob"));
const ManageApplications = lazy(() => import("./pages/employer/ManageApplications"));
const AIInterviews = lazy(() => import("./pages/employer/AIInterviews"));
const InterviewLinks = lazy(() => import("./pages/employer/InterviewLinks"));
const CandidateProfile = lazy(() => import("./pages/employer/CandidateProfile"));

// Admin pages
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminEmployers = lazy(() => import("./pages/admin/AdminEmployers"));
const AdminCandidates = lazy(() => import("./pages/admin/AdminCandidates"));
const AdminJobs = lazy(() => import("./pages/admin/AdminJobs"));
const AdminEmployerApprovals = lazy(() => import("./pages/admin/AdminEmployerApprovals"));
const AdminJobApprovals = lazy(() => import("./pages/admin/AdminJobApprovals"));
const AdminApplications = lazy(() => import("./pages/admin/AdminApplications"));
const AdminAnalytics = lazy(() => import("./pages/admin/AdminAnalytics"));
const AdminAssign = lazy(() => import("./pages/admin/AdminAssign"));
const AdminConsultants = lazy(() => import("./pages/admin/AdminConsultants"));
const AdminConsultantCompanies = lazy(() => import("./pages/admin/AdminConsultantCompanies"));
const AdminApprenticeshipContracts = lazy(() => import("./pages/admin/AdminApprenticeshipContracts"));
const AdminConsultantCandidates = lazy(() => import("./pages/admin/AdminConsultantCandidates"));
const AdminConsultantDocumentRequirements = lazy(() => import("./pages/admin/AdminConsultantDocumentRequirements"));
const AdminBranchManagement = lazy(() => import("./pages/admin/AdminBranchManagement"));
const ConsultantPostJob = lazy(() => import("./pages/admin/ConsultantPostJob"));
const ConsultantAIInterviews = lazy(() => import("./pages/admin/ConsultantAIInterviews"));
const ConsultantManageApplications = lazy(() => import("./pages/admin/ConsultantManageApplications"));
const ConsultantInterviewLinks = lazy(() => import("./pages/admin/ConsultantInterviewLinks"));
const ConsultantCandidateProfile = lazy(() => import("./pages/admin/ConsultantCandidateProfile"));
const AdminCRM = lazy(() => import("./pages/admin/AdminCRM"));
const ConsultantUserManagement = lazy(() => import("./pages/admin/consultant-user-management/ConsultantUserManagement"));
const ConsultantDashboard = lazy(() => import("./pages/consultant/ConsultantDashboard"));
const CRMCampaignsPage = lazy(() => import("./pages/admin/crm-routes/CRMCampaignsPage"));
const CRMLeadsPage = lazy(() => import("./pages/admin/crm-routes/CRMLeadsPage"));
const CRMKanbanPage = lazy(() => import("./pages/admin/crm-routes/CRMKanbanPage"));
const CRMAnalyticsPage = lazy(() => import("./pages/admin/crm-routes/CRMAnalyticsPage"));
const CRMTasksPage = lazy(() => import("./pages/admin/crm-routes/CRMTasksPage"));
const CRMEmailTemplatesPage = lazy(() => import("./pages/admin/crm-routes/CRMEmailTemplatesPage"));
const RBACDashboard = lazy(() => import("./pages/admin/RBACDashboard"));
const UserManagement = lazy(() => import("./pages/admin/UserManagement"));
const RoleManagement = lazy(() => import("./pages/admin/RoleManagement"));
const PermissionManagement = lazy(() => import("./pages/admin/permission-management/PermissionManagement"));
const ModuleManagement = lazy(() => import("./pages/admin/ModuleManagement"));
const ScreenManagement = lazy(() => import("./pages/admin/ScreenManagement"));
const PermissionAdminManagement = lazy(() => import("./pages/admin/PermissionAdminManagement"));
const KanbanStatusManagement = lazy(() => import("./pages/admin/KanbanStatusManagement"));
const AdminWebsiteContent = lazy(() => import("./pages/admin/AdminWebsiteContent"));
const AdminAboutPage = lazy(() => import("./pages/admin/AdminAboutPage"));
const AdminContactPage = lazy(() => import("./pages/admin/AdminContactPage"));
const AdminNextGenTrainingPage = lazy(() => import("./pages/admin/AdminNextGenTrainingPage"));
const AdminContactQueries = lazy(() => import("./pages/admin/AdminContactQueries"));
const AdminProfile = lazy(() => import("./pages/admin/AdminProfile"));
const AdminSmtpSettings = lazy(() => import("./pages/admin/AdminSmtpSettings"));
const AdminAssignCompaniesToStaff = lazy(() => import("./pages/admin/AdminAssignCompaniesToStaff"));
const AdminAssignCandidatesToStaff = lazy(() => import("./pages/admin/AdminAssignCandidatesToStaff"));

// AppContent component to use useLocation
function AppContent() {
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Check if current path is an authentication page or admin page
  const isAuthPage =
    location.pathname.includes("/candidate/signin") ||
    location.pathname.includes("/candidate/signup") ||
    location.pathname.includes("/employer/signin") ||
    location.pathname.includes("/employer/signup") ||
    location.pathname.includes("/admin-login") ||
    location.pathname.startsWith("/forgot-password") ||
    location.pathname.startsWith("/reset-password") ||
    location.pathname.startsWith("/admin/") ||
    location.pathname.startsWith("/employer/") ||
    location.pathname.startsWith("/consultant/") ||
    location.pathname.includes("/candidate/interview/start") ||
    location.pathname.startsWith("/interview/public") ||
    location.pathname.startsWith("/uploads/resumes/");

  useEffect(() => {
    // Simulate preloader
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  // Toggle mobile menu
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  // Close mobile menu
  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  // Add/remove body class when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.classList.add("mobile-menu-active");
    } else {
      document.body.classList.remove("mobile-menu-active");
    }
  }, [mobileMenuOpen]);

  return (
    <div className="App">
      {loading && <Preloader />}

      {/* Conditionally render Header and MobileHeader */}
      {!isAuthPage && (
        <>
          <Header
            toggleMobileMenu={toggleMobileMenu}
            mobileMenuOpen={mobileMenuOpen}
          />
          <MobileHeader
            isOpen={mobileMenuOpen}
            closeMobileMenu={closeMobileMenu}
          />

          {/* Body overlay for mobile menu */}
          {mobileMenuOpen && (
            <div className="body-overlay-1" onClick={closeMobileMenu}></div>
          )}
        </>
      )}

      <Suspense fallback={<Preloader />}>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Home />} />
          {/* <Route path="/job-grid" element={<JobGrid />} /> */}
          <Route path="/browse-jobs" element={<BrowseJobs />} />
          <Route path="/job/:id" element={<JobDetails />} />
          <Route path="/locations/:location" element={<LocationsJobs />} />
          <Route path="/company/:id" element={<CompanyDetails />} />
          <Route path="/candidates-grid" element={<CandidatesGrid />} />

          <Route path="/blog-grid" element={<BlogGrid />} />
          <Route path="/blog/:id" element={<BlogDetail />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/next-gen-training" element={<NextGenTraining />} />
          <Route path="/contact-us" element={<ContactUs />} />

          {/* Public Interview Link Route */}

          <Route
            path="/consultant/dashboard"
            element={
              <ProtectedRoute
                requiredPermissions={["consultant_dashboard.view", "staff_dashboard.view"]}
              >
                <ConsultantDashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/interview/public/:jobId/:token"
            element={<PublicInterviewLanding />}
          />

          {/* Authentication Routes */}
          <Route path="/candidate/signin" element={<CandidateSignIn />} />
          <Route path="/candidate/signup" element={<CandidateSignUp />} />
          <Route path="/employer/signin" element={<EmployerSignIn />} />
          <Route path="/employer/signup" element={<EmployerSignUp />} />
          <Route path="/admin-login" element={<AdminLogin />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />


          {/* Unauthorized Route */}
          <Route path="/unauthorized" element={<Unauthorized />} />

          {/* Admin Routes (Protected) */}
          <Route
            path="/admin/dashboard"
            element={
              <ProtectedRoute requiredPermissions={["admin_dashboard.view"]}>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/employers"
            element={
              <ProtectedRoute requiredPermissions={["employers.view"]}>
                <AdminEmployers />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/candidates"
            element={
              <ProtectedRoute requiredPermissions={["candidates.view"]}>
                <AdminCandidates />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/consultants"
            element={
              <ProtectedRoute requiredPermissions={["consultants.view"]}>
                <AdminConsultants />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/jobs"
            element={
              <ProtectedRoute requiredPermissions={["jobs.view", "approve_jobposting.view", "seeall_job.create"]}>
                <AdminJobs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/employer-approvals"
            element={
              <ProtectedRoute requiredPermissions={["approve_employer.view"]}>
                <AdminEmployerApprovals />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/job-approvals"
            element={
              <ProtectedRoute requiredPermissions={["approve_jobposting.view"]}>
                <AdminJobApprovals />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/applications"
            element={
              <ProtectedRoute requiredPermissions={["applications.view"]}>
                <AdminApplications />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/assign"
            element={
              <ProtectedRoute requiredPermissions={["assignments.view"]}>
                <AdminAssign />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/assign-companies-to-staff"
            element={
              <ProtectedRoute
                requiredPermissions={["assign_company.view", "assign_company.create"]}
              >
                <AdminAssignCompaniesToStaff />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/assign-candidates-to-staff"
            element={
              <ProtectedRoute
                requiredPermissions={["assign_candidate.view", "assign_candidate.create"]}
              >
                <AdminAssignCandidatesToStaff />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/consultant-companies"
            element={
              <ProtectedRoute requiredPermissions={["consultant_companies.view"]}>
                <AdminConsultantCompanies />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/apprenticeship-contracts"
            element={
              <ProtectedRoute requiredPermissions={["contract.view"]}>
                <AdminApprenticeshipContracts />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/branches"
            element={
              <ProtectedRoute requiredPermissions={["onboard_branch.view"]}>
                <AdminBranchManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/consultant-document-requirements"
            element={
              <ProtectedRoute requiredPermissions={["documents.view"]}>
                <AdminConsultantDocumentRequirements />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/consultant-candidates"
            element={
              <ProtectedRoute
                requiredPermissions={["consultant_candidates.view"]}
              >
                <AdminConsultantCandidates />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/consultant-candidates/:id"
            element={
              <ProtectedRoute
                requiredPermissions={["consultant_candidates.view"]}
              >
                <ConsultantCandidateProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/crm"
            element={
              <ProtectedRoute
                requiredPermissions={["crm_leads.view", "crm_contacts.view"]}
              >
                <AdminCRM />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/analytics"
            element={
              <ProtectedRoute requiredPermissions={["analytics.view"]}>
                <AdminAnalytics />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/consultant-user-management"
            element={
              <ProtectedRoute requiredPermissions={["crm_users.view"]}>
                <ConsultantUserManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/crm/campaigns"
            element={
              <ProtectedRoute requiredPermissions={["crm_campaigns.view"]}>
                <CRMCampaignsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/crm/leads"
            element={
              <ProtectedRoute requiredPermissions={["crm_leads.view"]}>
                <CRMLeadsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/crm/kanban"
            element={
              <ProtectedRoute requiredPermissions={["crm_kanban.view"]}>
                <CRMKanbanPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/crm/analytics"
            element={
              <ProtectedRoute requiredPermissions={["crm_analytics.view"]}>
                <CRMAnalyticsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/crm/tasks"
            element={
              <ProtectedRoute requiredPermissions={["crm_tasks.view"]}>
                <CRMTasksPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/crm/templates"
            element={
              <ProtectedRoute requiredPermissions={["crm_templates.view"]}>
                <CRMEmailTemplatesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/rbac"
            element={
              <ProtectedRoute
                requiredPermissions={["roles.view", "permissions.view"]}
              >
                <RBACDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute requiredPermissions={["users_management.view"]}>
                <UserManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/roles"
            element={
              <ProtectedRoute requiredPermissions={["roles.view"]}>
                <RoleManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/permissions"
            element={
              <ProtectedRoute requiredPermissions={["permissions.view"]}>
                <PermissionManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/modules"
            element={
              <ProtectedRoute requiredPermissions={["modules.view"]}>
                <ModuleManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/screens"
            element={
              <ProtectedRoute requiredPermissions={["screens.view"]}>
                <ScreenManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/permissions-admin"
            element={
              <ProtectedRoute requiredPermissions={["permissions.view"]}>
                <PermissionAdminManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/crm/kanban-statuses"
            element={
              <ProtectedRoute requiredPermissions={["crm_kanban_statuses.view"]}>
                <KanbanStatusManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/consultant-post-job"
            element={
              <ProtectedRoute requiredPermissions={["consultant_job.create"]}>
                <ConsultantPostJob />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/consultant-manage-applications"
            element={
              <ProtectedRoute
                requiredPermissions={["consultant_interviews.view"]}
              >
                <ConsultantManageApplications />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/consultant-ai-interviews"
            element={
              <ProtectedRoute
                requiredPermissions={["consultant_interviews.view"]}
              >
                <ConsultantAIInterviews />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/consultant-interview-links"
            element={
              <ProtectedRoute
                requiredPermissions={["consultant_interviews.view"]}
              >
                <ConsultantInterviewLinks />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/consultant-candidate/:id"
            element={
              <ProtectedRoute
                requiredPermissions={["consultant_interviews.view"]}
              >
                <ConsultantCandidateProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/hrms/dashboard"
            element={
              <ProtectedRoute requiredPermissions={["hrms_dashboard.view"]}>
                <HRMSDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/accounts/dashboard"
            element={
              <ProtectedRoute requiredPermissions={["accounts_dashboard.view"]}>
                <AccountsDashboard />
              </ProtectedRoute>
            }
          />

          {/* Website Content Management Route */}
          <Route
            path="/admin/website-content"
            element={
              <ProtectedRoute requiredPermissions={["website_content.view"]}>
                <AdminWebsiteContent />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/about-page"
            element={
              <ProtectedRoute requiredPermissions={["website_content.view"]}>
                <AdminAboutPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/contact-page"
            element={
              <ProtectedRoute requiredPermissions={["website_content.view"]}>
                <AdminContactPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/next-gen-training-page"
            element={
              <ProtectedRoute requiredPermissions={["website_content.view"]}>
                <AdminNextGenTrainingPage />
              </ProtectedRoute>
            }
          />

          {/* Contact Queries Route */}
          <Route
            path="/admin/contact-queries"
            element={
              <ProtectedRoute requiredPermissions={["contact_us.view"]}>
                <AdminContactQueries />
              </ProtectedRoute>
            }
          />

          {/* Admin Profile Route */}
          <Route
            path="/admin/profile"
            element={
              <ProtectedRoute requiredPermissions={[]}>
                <AdminProfile />
              </ProtectedRoute>
            }
          />

          {/* SMTP / Email Settings */}
          <Route
            path="/admin/smtp-settings"
            element={
              <ProtectedRoute requiredPermissions={["forgetpassword_credentials.view"]}>
                <AdminSmtpSettings />
              </ProtectedRoute>
            }
          />

          {/* Employer Routes (Protected) */}
          <Route
            path="/employer/dashboard"
            element={
              <ProtectedRoute requiredPermissions={["analytics.view"]}>
                <DashboardOverview />
              </ProtectedRoute>
            }
          />
          <Route
            path="/employer/company-profile"
            element={
              <ProtectedRoute requiredPermissions={["company_profile.view"]}>
                <CompanyProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/employer/post-job"
            element={
              <ProtectedRoute
                requiredPermissions={[
                  "post_job.create",
                  "post_job.view",
                  "post_job.update",
                  "post_job.delete",
                ]}
              >
                <PostJob />
              </ProtectedRoute>
            }
          />
          <Route
            path="/employer/manage-applications"
            element={
              <ProtectedRoute requiredPermissions={["applications.view"]}>
                <ManageApplications />
              </ProtectedRoute>
            }
          />
          <Route
            path="/employer/ai-interviews"
            element={
              <ProtectedRoute requiredPermissions={["ai_interviews.view"]}>
                <AIInterviews />
              </ProtectedRoute>
            }
          />
          <Route
            path="/employer/interview-links"
            element={
              <ProtectedRoute requiredPermissions={["interview_links.view"]}>
                <InterviewLinks />
              </ProtectedRoute>
            }
          />
          <Route
            path="/employer/candidate/:id"
            element={
              <ProtectedRoute requiredPermissions={["candidate_profile.view"]}>
                <CandidateProfile />
              </ProtectedRoute>
            }
          />

          {/* Candidate Routes (Protected) */}
          <Route
            path="/candidate/profile"
            element={
              <ProtectedRoute requiredPermissions={["profile.view"]}>
                <ManageProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/candidate/applied-jobs"
            element={
              <ProtectedRoute requiredPermissions={["applications.view"]}>
                <AppliedJobs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/candidate/bookmarked-jobs"
            element={
              <ProtectedRoute requiredPermissions={["bookmarks.view"]}>
                <BookmarkedJobs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/candidate/resume-builder"
            element={
              <ProtectedRoute requiredPermissions={["resume.view"]}>
                <ResumeBuilder />
              </ProtectedRoute>
            }
          />
          <Route
            path="/candidate/interviews"
            element={
              <ProtectedRoute requiredPermissions={["candidate_interviews.view"]}>
                <CandidateInterviews />
              </ProtectedRoute>
            }
          />
          <Route
            path="/candidate/interview/start/:id"
            element={
              <ProtectedRoute requiredPermissions={["candidate_interviews.view"]}>
                <InterviewRoom />
              </ProtectedRoute>
            }
          />
          <Route
            path="/candidate/my-transactions"
            element={
              <ProtectedRoute requiredPermissions={["transactions.view"]}>
                <MyTransactions />
              </ProtectedRoute>
            }
          />
        </Routes>
      </Suspense>

      {/* Conditionally render Footer */}
      {!isAuthPage && <Footer />}

      {/* Toast notifications */}
      <ToastContainer
        position="top-right"
        autoClose={15000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss={false}
        draggable
        pauseOnHover
        theme="colored"
        style={{ zIndex: 999999 }}
        limit={5}
      />
    </div>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
